/* eslint-disable @typescript-eslint/typedef */
import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  ViewChild,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { MatSortModule } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Params, Router, RouterOutlet } from '@angular/router';
import { PopupService } from '@app/core/services/popup.service';
import { PermissionDirective } from '@app/features/admin/settings/permission/directives/permission.directive';
import { SettingsState } from '@app/features/admin/settings/state/settings.state';
import { ProfileState } from '@app/layout/common/profile/state/profile.state';
import { SubtitleHeaderComponent } from '@app/shared/components/subtitle-header/subtitle-header.component';
import { MonthROBTableConfig } from '@chartering/vessel-reports/vessel-reports.constants';
import { TranslocoModule, TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import {
  createAdvanceFilterConfig,
  ISharedAdvanceFilterConfig,
  SharedAdvanceFilterContainerComponent,
  SharedQueryAdvanceFilter
} from '@shared/components/filters';
import { TableComponent, TableConfig } from '@shared/components/table';
import { LIMIT_PER_PAGE_DEFAULT } from '@shared/shared.constants';
import { SafeAny } from '@shared/types/any.type';
import { SealFindByKeyPipe } from '@shipsfocus/seal';
import { ItineraryAction } from '@vessel-itinerary/state/itinerary.action';
import { RobReportDetailsComponent } from '@vessel-reports/rob/components/rob-report-details/rob-report-details.component';
import { RobReportDialogComponent } from '@vessel-reports/rob/components/rob-report-dialog/rob-report-dialog.component';
import { RobActions } from '@vessel-reports/rob/state/rob.action';
import { RobService } from '@vessel-reports/rob/state/rob.service';
import { RobState } from '@vessel-reports/rob/state/rob.state';
import { VesselReportsAction } from '@vessel-reports/state/vessel-reports.action';
import { VesselReportsService } from '@vessel-reports/state/vessel-reports.service';
import { VesselReportsState } from '@vessel-reports/state/vessel-reports.state';
import { VesselReport, VesselReportPagination } from '@vessel-reports/state/vessel-reports.types';
import { DatetimeLocalPipe } from 'app/core/pipe/datetime/datetime-local.pipe';
import { ExcelService } from 'app/core/services/excel.service';
import { filterQueries } from 'app/core/utils/helpers';
import { isEmpty } from 'lodash';
import { debounceTime, forkJoin, from, map, merge, Observable, Subject, switchMap, take, takeUntil, tap } from 'rxjs';

@Component({
  selector: 'rob',
  templateUrl: './rob.component.html',
  styles: [
    `
      .rob-grid {
        grid-template-columns: 150fr 150fr 150fr 200fr 577fr 150fr 24px;

        &-action {
          grid-template-columns: repeat(2, 1fr) 3.5rem 3rem;

          @screen sm {
            grid-template-columns: repeat(3, 1fr) 3.5rem 3rem;
          }

          @screen md {
            grid-template-columns: repeat(2, 1fr) 1.5fr repeat(2, 1fr) 3.5rem 3rem;
          }

          @screen lg {
            grid-template-columns: repeat(2, 1fr) 1.5fr repeat(3, 1fr) 1.5fr 3.5rem 3rem;
          }
        }
      }

      .mdc-list-item > span {
        width: auto !important;
      }
    `
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoPipe,
    NgClass,
    RouterOutlet,
    ReactiveFormsModule,
    TranslocoModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatSidenavModule,
    MatSortModule,
    MatTooltipModule,
    MatPaginator,
    SharedAdvanceFilterContainerComponent,
    TableComponent,
    PermissionDirective,
    MatProgressSpinner,
    SubtitleHeaderComponent
  ],
  providers: [DatetimeLocalPipe, SealFindByKeyPipe]
})
export class RobComponent implements OnInit, AfterViewInit, OnDestroy {
  protected readonly translateService: TranslocoService = inject(TranslocoService);
  protected readonly destroyRef: DestroyRef = inject(DestroyRef);
  protected readonly popupService: PopupService = inject(PopupService);
  @ViewChild('detailsDrawer', { static: true }) detailsDrawer: MatDrawer;
  @ViewChild(MatPaginator) private _paginator: MatPaginator;

  vesselReports: Signal<VesselReport[]> = computed(() => this.stateService.vesselReports());
  pagination: Signal<VesselReportPagination> = computed(() => this.stateService.pagination());
  selectedRobReport: WritableSignal<VesselReport> = signal(void 0);
  drawerType: WritableSignal<'view' | 'search'> = signal(void 0);
  queryParams: WritableSignal<Params> = signal(void 0);
  protected readonly downloadingExcel: Signal<boolean> = this._excelService.listenToJob('vessel_report', 'si_rob');
  private _profileState: ProfileState = inject(ProfileState);
  private _robReportActions: RobActions = inject(RobActions);
  private _itineraryActions: ItineraryAction = inject(ItineraryAction);
  private _robReportState: RobState = inject(RobState);
  private _robService: RobService = inject(RobService);
  reportPopupLoaded: boolean = false;
  currentStatusIndex: string;
  vesselReportsLoading: boolean = false;

  statuses = [
    { id: 1, name: 'Draft', type: 'warning' },
    { id: 2, name: 'Saved', type: 'success' },
    { id: 3, name: 'Submitted', type: 'info' }
  ];

  vesselTypes = [{ id: 6, name: 'CHEMICAL TANKER', color: 'text-blue-700 border-blue-700' }];

  months = [
    { value: 1, name: 'January' },
    { value: 2, name: 'February' },
    { value: 3, name: 'March' },
    { value: 4, name: 'April' },
    { value: 5, name: 'May' },
    { value: 6, name: 'June' },
    { value: 7, name: 'July' },
    { value: 8, name: 'August' },
    { value: 9, name: 'September' },
    { value: 10, name: 'October' },
    { value: 11, name: 'November' },
    { value: 12, name: 'December' }
  ];

  protected readonly advanceFilterConfig: ISharedAdvanceFilterConfig = createAdvanceFilterConfig(
    [
      {
        key: 'year',
        filterKey: 'filter[year]',
        titleKey: 'ship_interface.year',
        selectionType: 'year',
        displayName: 'year',
        customClasses: 'hidden md:block',
        menuType: 'single-selection',
        items: computed(() => this._robReportState.years())
      }
    ],
    this.queryParams,
    ['filter[report_type_id]']
  );
  protected readonly searchInputControl: UntypedFormControl = this.advanceFilterConfig.search.control;

  protected readonly queryAdvanceFilter: SharedQueryAdvanceFilter = (key: string, filterKey: string) => {
    return this._robService
      .getAdvanceFilters(key, 300, {
        ...filterQueries(this.queryParams(), filterKey),
        ...(filterKey === 'filter[report_type_id]' ? {} : !this.queryParams()?.['filter[report_type_id]'] ? { 'filter[report_type_id]': '9' } : {})
      })
      .pipe(
        map(({ data }: { data: SafeAny[] }) => {
          if (['year'].includes(key)) {
            return (data || [])
              ?.filter((item) => !!item.year)
              ?.map((item) => ({
                ...item,
                year: item.year?.toString()
              }));
          }

          return [];
        }),
        tap((response) => {
          if (key === 'year') {
            this._robReportState.set({
              years: response
            });
          }
        })
      );
  };

  protected readonly tableConfig: TableConfig<VesselReport> = {
    // template: [
    //   'lg:grid-cols-[150fr_150fr_150fr_200fr_577fr_150fr_48px]',
    //   'md:grid-cols-[150fr_150fr_150fr_200fr_150fr_48px]',
    //   'grid-cols-[150fr_150fr_200fr_48px]'
    // ],
    controller: true,
    header: {
      classes: 'sticky top-0 z-10 bg-white',
      sort: {}
    },
    body: {
      byRow: (row) => {
        return {
          classes: computed(
            () =>
              `hover:bg-slate-50 hover:cursor-pointer dark:hover:bg-hover ${this.selectedRobReport()?.id === row.id ? 'bg-primary-100 dark:bg-primary-900' : ''}`
          )
        };
      },
      permission: ['monthly_rob_ship_interface', ['view']],
      onClick: (row) => {
        this.selectReport(row.id);
      }
    },
    columns: [
      {
        key: 'type',
        header: {
          value: 'ship_interface.vessel_type',
          classes: 'hidden md:block'
        },
        body: {
          classes: 'hidden md:block w-full overflow-hidden truncate',
          wrapper: {
            by: 'type',
            classes: '!max-w-full truncate'
          },
          value: (row) => row?.vessel_type_name || '-'
        },
        controller: {
          label: 'ship_interface.vessel_type',
          template: {
            md: '150fr'
          }
        }
      },
      {
        key: 'year',
        header: {
          value: 'ship_interface.year'
        },
        body: {
          value: (row) => `${row?.year || '-'}`
        },
        controller: {
          label: 'ship_interface.year',
          disabled: true,
          template: {
            default: '150fr'
          }
        }
      },
      {
        key: 'month',
        header: {
          value: 'ship_interface.month'
        },
        body: {
          value: (row) => this.months?.find((month) => month?.value === row.month)?.name || '-'
        },
        controller: {
          label: 'ship_interface.month',
          disabled: true,
          template: {
            default: '150fr'
          }
        }
      },
      {
        key: 'date_report',
        header: {
          value: 'ship_interface.report_submitted'
        },
        body: {
          value: (row) =>
            row.date_report
              ? this._datetimeLocal.transform(row.date_report, {
                  format: 'DD MMM yyyy'
                })
              : '-'
        },
        controller: {
          label: 'ship_interface.report_submitted',
          disabled: true,
          template: {
            default: '200fr'
          }
        }
      },
      {
        key: 'remarks',
        header: {
          value: 'ship_interface.remarks',
          classes: 'hidden lg:block'
        },
        body: {
          value: (row) => row.remarks,
          classes: 'hidden lg:block'
        },
        controller: {
          label: 'ship_interface.remarks',
          template: {
            lg: '577fr'
          }
        }
      },
      {
        key: 'status',
        header: {
          value: 'ship_interface.status',
          classes: 'hidden md:block'
        },
        body: {
          value: (row) => this._sealFindByKey.transform(row.status_id, 'id', this.statuses)?.name,
          wrapper: {
            by: 'status',
            type: (row) => this._sealFindByKey.transform(row.status_id, 'id', this.statuses)?.type
          },
          classes: 'hidden md:block'
        },
        controller: {
          label: 'ship_interface.status',
          template: {
            md: '150fr'
          }
        }
      },
      {
        key: 'delete',
        header: {
          classes: 'w-12'
        },
        body: {
          classes: 'w-12 h-12',
          wrapper: {
            by: 'button-icon',
            icon: {
              value: 'heroicons_outline:trash',
              classes: 'icon-size-5'
            }
          },
          permission: ['monthly_rob_ship_interface', ['delete']],
          ignoreFromRowClick: true,
          onClick: (row) => this.deleteReport(row)
        },
        controller: {
          admin: true,
          template: '48px'
        }
      }
    ],
    render: 'grid'
  };

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _changeDetectorRef: ChangeDetectorRef,
    private _actionService: VesselReportsAction,
    public stateService: VesselReportsState,
    private _injector: Injector,
    private _settingsStateService: SettingsState,
    private _vesselReportsService: VesselReportsService,
    private _matDialog: MatDialog,
    private _excelService: ExcelService,
    private _datetimeLocal: DatetimeLocalPipe,
    private _sealFindByKey: SealFindByKeyPipe
  ) {}

  ngOnInit(): void {
    this._subscribeToSource$(this._vesselReportsService.vesselReportsLoading$, (vesselReportsLoading) => {
      this.vesselReportsLoading = vesselReportsLoading;
    });

    this._subscribeToSource$(this._activatedRoute.paramMap, (paramMap) => {
      this.currentStatusIndex = paramMap.get('status');
    });

    effect(
      () => {
        this.selectedRobReport.set({ ...this._robReportState.detailReport() });

        if (this._robReportState.detailReport()) {
          this.drawerType.set('view');
        }
      },
      { injector: this._injector }
    );

    this._activatedRoute.queryParams.pipe(takeUntil(this._unsubscribeAll)).subscribe((queryParams) => {
      this.queryParams.set({ ...queryParams });

      if (isEmpty(this.queryParams())) {
        void this._router.navigate([], {
          queryParams: {
            'filter[report_type_id]': '9'
          },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    });

    this.detailsDrawer.openedChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((opened) => {
      if (!opened) {
        this.selectedRobReport.set(null);
        this.drawerType.set(null);
      }
    });
  }

  ngAfterViewInit(): void {
    if (!this._paginator) {
      return;
    }
    const searchChange$ = this.searchInputControl.valueChanges.pipe(
      debounceTime(500),
      tap(() => (this._paginator.pageIndex = 0)),
      takeUntil(this._unsubscribeAll)
    );

    merge(this._paginator.page, searchChange$)
      .pipe(
        switchMap(() => {
          const queryParams = {
            ...this.queryParams(),
            'filter[search]': this.searchInputControl.value,
            'filter[report_type_id]': 9
          };

          return this._router.navigate(['./'], {
            relativeTo: this._activatedRoute,
            queryParams: {
              ...queryParams,
              page: this._paginator.pageIndex + 1,
              limit: this._paginator.pageSize
            }
          });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this._actionService.actions.getVesselReports({
          queryParams: {
            ...this.queryParams(),
            ...{
              'filter[report_type_id]': 9
            }
          }
        });
      });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  backdropClicked(): void {
    if (this.drawerType() === 'view') {
      this._router.navigate(['./'], { relativeTo: this._activatedRoute, queryParams: this.queryParams() }).then();
    }

    if (this.drawerType() === 'search') {
      this.detailsDrawer?.close()?.then();
    }

    this._changeDetectorRef.markForCheck();
  }

  selectReport(id: string): void {
    this._robReportActions.actions.cleanState();
    this._robReportActions.actions.getInventories({ queryParams: {} });
    this._itineraryActions.actions.getVesselMovements({ vessel_id: null });
    this._robReportActions.actions.getDetailReport({ report_id: id });
    forkJoin([
      this._robReportActions.actions.getInventoriesSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)),
      this._robReportActions.actions.getDetailReportSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll))
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.reportPopupLoaded) {
          return;
        }
        const dialog = this._matDialog.open(RobReportDetailsComponent, {
          data: {
            type: 'details'
          }
        });
        this.reportPopupLoaded = true;
        dialog
          .afterClosed()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this._robReportState.set({
              detailReport: null
            });
            this.reportPopupLoaded = false;
          });
      });
  }

  createReport(): void {
    this._robReportActions.actions.getPreviousReport({
      vessel_id: this._profileState.profile()?.id?.toString(),
      type: 9
    });
    this._robReportActions.actions.getPreviousReportSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
      if (this.reportPopupLoaded) {
        return;
      }
      const dialog = this._matDialog.open(RobReportDialogComponent);
      this.reportPopupLoaded = true;
      dialog
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.reportPopupLoaded = false;
        });
    });
  }

  exportMonthlyROB(): void {
    const queryParams = filterQueries(this.queryParams());
    this._vesselReportsService
      .getExcelMonthlyRobs(queryParams)
      .pipe(
        take(1),
        switchMap((excelData: VesselReport[]) =>
          from(
            this._excelService.generateExcel(
              [
                {
                  title: this.translateService.translate('ship_interface.port_report'),
                  tabColor: '#4FC3F7',
                  header: {
                    font: { size: 12, bold: true, color: '#FFFFFF' },
                    alignment: { horizontal: 'center' },
                    fill: { pattern: 'solid', color: '#3949AB' }
                  },
                  table: {
                    config: MonthROBTableConfig,
                    data: excelData?.map((item: VesselReport) => ({
                      ...item,
                      vessel_type_id: this.vesselTypes.find((type) => type?.id === item?.vessel_type_id)?.name,
                      month: this.months.find((month) => month?.value === item?.month)?.name,
                      status_id: this.statuses.find((status) => status?.id === item?.status_id).name
                    }))
                  },
                  uppercase: true
                }
              ],
              `Vessel_Report_Month_ROB`
            )
          )
        ),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe();
  }

  deleteReport(report: VesselReport): void {
    this.popupService.openConfirmationDialog({
      title: this.translateService.translate('ship_interface.delete_report'),
      message: this.translateService.translate('ship_interface.are_you_sure_you_want_to_delete_this_report_this_action_cannot_be_undone'),
      actionType: 'delete',
      onAccept: () => {
        this._actionService.actions.deleteVesselReport({
          id: report.id,
          type: this.currentStatusIndex,
          activatedRoute: this._activatedRoute
        });
        this._actionService.actions.deleteVesselReportSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
          this._actionService.actions.getVesselReports({
            type: this.currentStatusIndex,
            queryParams: this.queryParams()
          });
        });
      },
      onCancel: () => {}
    });
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  navigateWithParams(additionalParams = {}): void {
    this._router
      .navigate(['./'], {
        relativeTo: this._activatedRoute,
        queryParams: {
          page: '1',
          limit: this.queryParams()?.['limit'] || LIMIT_PER_PAGE_DEFAULT,
          sort: this.queryParams()?.['sort'] || '',
          ...additionalParams
        }
      })
      .then(() => {
        this._actionService.actions.getVesselReports({
          queryParams: {
            ...this.queryParams(),
            'filter[report_type_id]': 9
          }
        });
      });
  }

  toggleSidebar(): void {
    this.stateService.set({ openedSidebarDrawer: !this.stateService.openedSidebarDrawer() });
  }

  private _subscribeToSource$<T>(source$: Observable<T>, callback: (value: T) => void): void {
    source$.pipe(takeUntil(this._unsubscribeAll)).subscribe((value: T) => {
      callback(value);

      this._changeDetectorRef.markForCheck();
    });
  }

  exportToExcel(): void {
    if (this.downloadingExcel()) {
      return;
    }
    this._excelService.triggerJobFlowAsync('vessel_report', this.queryParams(), 'si_rob');
  }
}
